/**
 * Reports Service
 *
 * Service layer for Skier Resort View report API calls.
 */
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Fetches rolling 24-hour live skier resort data.
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchSkierResortViewLive = async (addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/skier_resort_view/live'), {
      method: 'POST',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch live skier data: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching live skier resort data', error.stack, 'Skier Resort View');
    console.error('Error fetching skier resort view live:', error);
    throw error;
  }
};

/**
 * Fetches season ski resort report data for a given date range.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchSeasonSkiResortReport = async (startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/season_ski_resort_report'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch season ski resort report: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching season ski resort report', error.stack, 'Season Ski Resort Report');
    console.error('Error fetching season ski resort report:', error);
    throw error;
  }
};

/**
 * Fetches every skier's days/hours across all resorts for a date range — the
 * table under the Season tab when no resort row is selected.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchSeasonSkiResortSkiers = async (startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/season_ski_resort_report/skiers'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch skiers: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching season skiers', error.stack, 'Season Ski Resort Report');
    console.error('Error fetching season ski resort skiers:', error);
    throw error;
  }
};

/**
 * Fetches every skier's days/hours for one resort and date range — the
 * drill-down table under a Season-tab resort row.
 * @param {string} resortId
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchSeasonSkiResortResortDays = async (resortId, startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/season_ski_resort_report/resort'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ resortId, startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch resort ski days: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching season resort ski days', error.stack, 'Season Ski Resort Report');
    console.error('Error fetching season ski resort resort days:', error);
    throw error;
  }
};

/**
 * Fetches goggles connectivity report data (error 5012) for a given date range.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
/**
 * Fetches waypoints usage report data for a given date range.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchWaypointsUsage = async (startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/waypoints_usage'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch waypoints usage: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching waypoints usage report', error.stack, 'Waypoints Usage');
    console.error('Error fetching waypoints usage report:', error);
    throw error;
  }
};

/**
 * Fetches goggles SW adoption report data (install base, unique ski days, per-user rows).
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchGogglesSWAdoption = async (startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/goggles_sw_adoption'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch goggles SW adoption: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching goggles SW adoption report', error.stack, 'Goggles SW Adoption');
    console.error('Error fetching goggles SW adoption report:', error);
    throw error;
  }
};

/**
 * Fetches the Goggles Usage report — connected hours per day, plus the per-day
 * per-user table the chart drills into.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 * @param {object} filters - { iosVersions, androidVersions, gogglesFirmwareVersions }
 */
export const fetchGogglesUsage = async (startDate, endDate, addError = null, filters = {}) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const body = { startDate, endDate };
    // Both must be sent together — an empty array for one platform means
    // "exclude that platform," not "no restriction" (see backend/reports/
    // goggles_usage/index.js header comment).
    if (filters.iosVersions && filters.androidVersions) {
      body.iosVersions = filters.iosVersions;
      body.androidVersions = filters.androidVersions;
    }
    if (filters.gogglesFirmwareVersions?.length) body.gogglesFirmwareVersions = filters.gogglesFirmwareVersions;

    const response = await fetch(buildApiUrl('/reports/goggles_usage'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch goggles usage: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching goggles usage report', error.stack, 'Goggles Usage');
    console.error('Error fetching goggles usage:', error);
    throw error;
  }
};

export const fetchGogglesConnectivity = async (startDate, endDate, addError = null, filters = {}) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const body = { startDate, endDate };
    // Both must be sent together — an empty array for one platform means
    // "exclude that platform," not "no restriction" (see backend/reports/
    // goggles_connectivity/index.js header comment).
    if (filters.iosVersions && filters.androidVersions) {
      body.iosVersions = filters.iosVersions;
      body.androidVersions = filters.androidVersions;
    }
    if (filters.gogglesFirmwareVersions?.length) body.gogglesFirmwareVersions = filters.gogglesFirmwareVersions;
    if (filters.causeKey) body.causeKey = filters.causeKey;
    if (filters.excludeLowRssi === false) body.excludeLowRssi = false;

    const response = await fetch(buildApiUrl('/reports/goggles_connectivity'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch goggles connectivity: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching goggles connectivity report', error.stack, 'Goggles Connectivity');
    console.error('Error fetching goggles connectivity report:', error);
    throw error;
  }
};

/**
 * Fetches OTA attempts report data (daily OTASuccess / OTAFailed counts).
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 */
/**
 * Fetches every OTA attempt on a single day — the drill-down table under the
 * daily chart. `startDate`/`endDate` are the range the chart was built from;
 * they clamp the backend's table scan so the rows match the clicked bar.
 *
 * @param {string} date       - YYYY-MM-DD, the day that was clicked
 * @param {string} startDate  - YYYY-MM-DD range start of the chart
 * @param {string} endDate    - YYYY-MM-DD range end of the chart
 * @param {Function} addError - Optional error handler from ErrorContext
 */
export const fetchOtaAttemptsDay = async (date, startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/ota_attempts/day'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ date, startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch OTA attempts for ${date}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching OTA attempts for day', error.stack, 'OTA Attempts');
    console.error('Error fetching OTA attempts day drill-down:', error);
    throw error;
  }
};

export const fetchOtaAttempts = async (startDate, endDate, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const response = await fetch(buildApiUrl('/reports/ota_attempts'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ startDate, endDate })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch OTA attempts: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching OTA attempts report', error.stack, 'OTA Attempts');
    console.error('Error fetching OTA attempts report:', error);
    throw error;
  }
};

/**
 * Fetches Firebase error report data (events flagged with a firebase_error
 * param, grouped by error code and by event) for a given date range.
 *
 * `filters`, when passed, activates iOS/Android version filtering — an empty
 * array for one platform excludes that platform rather than leaving it
 * unrestricted, so omit `filters` entirely for the first, option-discovering
 * fetch and only pass it once the version pickers are in play.
 *
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 * @param {{iosVersions: string[], androidVersions: string[]}|null} filters
 */
/**
 * Fetches Auth Issues report data — login, sign-up, account-deletion, and
 * own-profile-load failures, combined from GA4 Analytics and Crashlytics.
 * See docs/features/analytics.md and plans/auth-issues/eng-plan.md §3.1.
 *
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 * @param {{appVersions?: string[], sourceKey?: string}} [filters]
 */
export const fetchAuthIssues = async (startDate, endDate, addError = null, filters = {}) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const body = { startDate, endDate };
    if (filters.appVersions?.length) body.appVersions = filters.appVersions;
    if (filters.sourceKey) body.sourceKey = filters.sourceKey;

    const response = await fetch(buildApiUrl('/reports/auth_issues'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch auth issues: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching auth issues report', error.stack, 'Auth Issues');
    console.error('Error fetching auth issues report:', error);
    throw error;
  }
};

/**
 * Fetches Mini Map report data — map vector-tile (.bin) BLE transfer outcome,
 * failure reason, transfer duration, and resort breakdown.
 * See docs/features/analytics.md and plans/mini-map/eng-plan.md §3.1.
 *
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError - Optional error handler from ErrorContext
 * @param {{appVersions?: string[], gogglesFirmwareVersions?: string[]}} [filters]
 */
export const fetchMiniMap = async (startDate, endDate, addError = null, filters = {}) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const body = { startDate, endDate };
    if (filters.appVersions?.length) body.appVersions = filters.appVersions;
    if (filters.gogglesFirmwareVersions?.length) body.gogglesFirmwareVersions = filters.gogglesFirmwareVersions;

    const response = await fetch(buildApiUrl('/reports/mini_map'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch Mini Map report: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching Mini Map report', error.stack, 'Mini Map');
    console.error('Error fetching Mini Map report:', error);
    throw error;
  }
};

export const fetchFirebaseError = async (startDate, endDate, addError = null, filters = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const body = { startDate, endDate };
    if (filters) {
      body.iosVersions = filters.iosVersions || [];
      body.androidVersions = filters.androidVersions || [];
    }

    const response = await fetch(buildApiUrl('/reports/firebase_error'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch Firebase error report: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching Firebase error report', error.stack, 'Firebase Errors');
    console.error('Error fetching Firebase error report:', error);
    throw error;
  }
};

/**
 * Fetches BLE Serial Log Uploads report data (successful GogglesLogCaptured
 * events, one row per capture).
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {Function} addError
 * @param {{ iosVersions: string[], androidVersions: string[], gogglesFirmwareVersions: string[] } | null} filters
 *   null runs unfiltered (the option-discovering first fetch).
 */
export const fetchBleSerialLogUploads = async (startDate, endDate, addError = null, filters = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Not authenticated. Please log in.');

    const body = { startDate, endDate };
    if (filters) {
      body.iosVersions = filters.iosVersions || [];
      body.androidVersions = filters.androidVersions || [];
      body.gogglesFirmwareVersions = filters.gogglesFirmwareVersions || [];
    }

    const response = await fetch(buildApiUrl('/reports/ble_serial_log_uploads'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch BLE serial log uploads: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || 'Error fetching BLE serial log uploads', error.stack, 'BLE Serial Log Uploads');
    console.error('Error fetching BLE serial log uploads:', error);
    throw error;
  }
};
